/**
 * Minimal DXF R12 writer — LINEs and TEXTs on named layers. R12 is the
 * lingua franca every CAD/CAM package still reads. Coordinates in mm,
 * Y up (DXF native).
 */

import type { SheetModel } from '../drawing/layout';

const LAYERS: [keyof Pick<SheetModel, 'visible' | 'hidden' | 'section' | 'hatch' | 'dims' | 'frame'>, string, number][] = [
  ['visible', 'VISIBLE', 7],
  ['hidden', 'HIDDEN', 8],
  ['section', 'SECTION', 1],
  ['hatch', 'HATCH', 8],
  ['dims', 'DIMS', 3],
  ['frame', 'SHEET', 5],
];

export function sheetToDxf(model: SheetModel): string {
  const out: string[] = [];
  const w = (code: number, value: string | number): void => {
    out.push(String(code), String(value));
  };

  w(0, 'SECTION'); w(2, 'HEADER');
  w(9, '$INSUNITS'); w(70, 4); // millimetres
  w(0, 'ENDSEC');

  w(0, 'SECTION'); w(2, 'TABLES');
  w(0, 'TABLE'); w(2, 'LAYER'); w(70, LAYERS.length);
  for (const [, name, color] of LAYERS) {
    w(0, 'LAYER'); w(2, name); w(70, 0); w(62, color);
    w(6, name === 'HIDDEN' ? 'DASHED' : 'CONTINUOUS');
  }
  w(0, 'ENDTAB');
  // Linetype table so DASHED resolves.
  w(0, 'TABLE'); w(2, 'LTYPE'); w(70, 2);
  w(0, 'LTYPE'); w(2, 'CONTINUOUS'); w(70, 0); w(3, 'Solid'); w(72, 65); w(73, 0); w(40, 0);
  w(0, 'LTYPE'); w(2, 'DASHED'); w(70, 0); w(3, 'Dashed'); w(72, 65); w(73, 2); w(40, 4);
  w(49, 3); w(49, -1);
  w(0, 'ENDTAB');
  w(0, 'ENDSEC');

  w(0, 'SECTION'); w(2, 'ENTITIES');
  for (const [key, layer] of LAYERS) {
    const segs = model[key];
    for (let i = 0; i + 3 < segs.length; i += 4) {
      w(0, 'LINE'); w(8, layer);
      w(10, r(segs[i]!)); w(20, r(segs[i + 1]!)); w(30, 0);
      w(11, r(segs[i + 2]!)); w(21, r(segs[i + 3]!)); w(31, 0);
    }
  }
  for (const t of model.texts) {
    w(0, 'TEXT'); w(8, 'DIMS');
    w(10, r(t.p[0])); w(20, r(t.p[1])); w(30, 0);
    w(40, t.size);
    w(1, t.text.replace('⌀', '%%c')); // DXF diameter symbol
    w(72, t.anchor === 'middle' ? 1 : t.anchor === 'end' ? 2 : 0);
    w(11, r(t.p[0])); w(21, r(t.p[1])); w(31, 0);
  }
  w(0, 'ENDSEC');
  w(0, 'EOF');
  return out.join('\n') + '\n';
}

const r = (v: number): number => Math.round(v * 1000) / 1000;
